import { useMemo } from "react";

import type { DiningAttendeesModal_booking$data } from "../__generated__/DiningAttendeesModal_booking.graphql";
import type { Attendee } from "../components/types";
import { areIdSetsEqual, buildDisplayName } from "../utils/attendeeUtils";

type Mode =
  | { type: "list" }
  | { type: "add" }
  | { type: "replace"; targetPlayerId: string };

type InitialSnapshot = {
  ownerId: string | null;
  attendeeIds: string[];
};

/**
 * Extracts attendees from booking rounds data
 */
export const useBookingAttendees = (
  booking: DiningAttendeesModal_booking$data | null
): Attendee[] => {
  return useMemo<Attendee[]>(() => {
    const players: Attendee[] = [];
    booking?.rounds?.edges?.forEach((edge) => {
      const node = edge?.node;
      const player = node?.player;
      const playerId = player?.id;

      if (!node || !player || !playerId) {
        return;
      }

      const playerType =
        player.__typename === "Member"
          ? "Member"
          : player.__typename === "Customer"
          ? "Customer"
          : player.__typename === "Guest"
          ? "Guest"
          : "Unknown";

      players.push({
        playerId,
        displayName: buildDisplayName({
          fullName: player.fullName,
          firstName: player.firstName,
          lastName: player.lastName,
        }),
        firstName: player.firstName ?? null,
        lastName: player.lastName ?? null,
        profileImagePublicId: player.profileImage?.publicId ?? null,
        playerType,
      });
    });

    return players;
  }, [booking?.rounds]);
};

/**
 * Extracts owner ID from booking ownerClient
 */
export const useOwnerIdFromBooking = (
  booking: DiningAttendeesModal_booking$data | null
): string | null => {
  return useMemo(() => {
    const ownerClient = booking?.ownerClient;
    if (
      ownerClient?.__typename === "Member" ||
      ownerClient?.__typename === "Customer"
    ) {
      return ownerClient.id ?? null;
    }

    return null;
  }, [booking?.ownerClient]);
};

/**
 * Sorts attendees with owner first
 */
export const useDisplayAttendees = (
  attendees: Attendee[],
  ownerId: string | null
): Attendee[] => {
  return useMemo(() => {
    if (!ownerId) {
      return attendees;
    }

    const owner = attendees.find((attendee) => attendee.playerId === ownerId);
    const others = attendees.filter(
      (attendee) => attendee.playerId !== ownerId
    );
    return owner ? [owner, ...others] : attendees;
  }, [attendees, ownerId]);
};

/**
 * Derived state for attendee management
 */
export const useAttendeeDerivedState = (
  attendees: Attendee[],
  ownerId: string | null,
  mode: Mode,
  maxPartySize: number | null
) => {
  const attendeeIds = useMemo(
    () => attendees.map((attendee) => attendee.playerId),
    [attendees]
  );

  const selectedIdsForAddPatrons = useMemo(() => {
    if (mode.type === "replace") {
      return attendeeIds.filter((id) => id !== mode.targetPlayerId);
    }
    return attendeeIds;
  }, [attendeeIds, mode]);

  const targetAttendee = useMemo(() => {
    if (mode.type === "replace") {
      return (
        attendees.find(
          (attendee) => attendee.playerId === mode.targetPlayerId
        ) ?? null
      );
    }
    return null;
  }, [attendees, mode]);

  const isAtCapacity = useMemo(
    () => (maxPartySize != null ? attendees.length >= maxPartySize : false),
    [attendees.length, maxPartySize]
  );

  const remainingSlots = useMemo(
    () =>
      maxPartySize != null
        ? Math.max(maxPartySize - attendees.length, 0)
        : null,
    [attendees.length, maxPartySize]
  );

  return {
    attendeeIds,
    selectedIdsForAddPatrons,
    targetAttendee,
    isAtCapacity,
    remainingSlots,
  };
};

/**
 * Checks if attendee state has changed from initial snapshot
 */
export const useIsDirty = (
  ownerId: string | null,
  attendeeIds: string[],
  initialSnapshot: InitialSnapshot | null
): boolean => {
  return useMemo(() => {
    if (!initialSnapshot) {
      return false;
    }
    return (
      ownerId !== initialSnapshot.ownerId ||
      !areIdSetsEqual(attendeeIds, initialSnapshot.attendeeIds)
    );
  }, [attendeeIds, initialSnapshot, ownerId]);
};
